import { RibbonButton, RibbonSection, RibbonSlot, Select } from "@tredespace/ui/widgets";
import type { JSX } from "react";
import type { AccessKind, RailKind } from "../../engine/tools/types.ts";
import type { BracketKind } from "../../model/types.ts";
import { planesState } from "../../state/planes/planes.state.ts";
import {
  setAccessKind,
  setAutoSpir,
  setBeamKind,
  setBracketKind,
  setRailKind,
  setSize,
  setSnap,
  sizesFor,
} from "../../state/tool/tool.actions.ts";
import { toolState } from "../../state/tool/tool.state.ts";
import { deckOptionSlots } from "./DeckOptions.tsx";
import { planeOptionButtons } from "./PlaneOptions.tsx";

const ACCESS_KINDS: readonly { value: AccessKind; label: string }[] = [
  { value: "luke", label: "Hatch + ladder" },
  { value: "utv", label: "UTV stair" },
  { value: "ladder", label: "Ladder ST 2100" },
];

const RAIL_KINDS: readonly { value: RailKind; label: string }[] = [
  { value: "both", label: "Frame + toe" },
  { value: "skrd", label: "SKRD only" },
  { value: "toe", label: "Toe board only" },
];

const BRACKET_KINDS: readonly { value: BracketKind; label: string }[] = [
  { value: "sk", label: "SK with spigot" },
  { value: "skd", label: "SKD 1250 + beam" },
];

/** Contextual options — only what the active tool actually uses is shown. */
export function OptionsSection(): JSX.Element | null {
  const state = toolState.use();
  const { tool, size, beamKind, accKind, railKind, bracketKind, autoSpir, snap } = state;
  const planes = planesState.use();
  const sizes = sizesFor(state);
  const showsBeamKind = tool === "beam" || (tool === "bracket" && bracketKind === "skd");
  const showsAnything = sizes !== null || showsBeamKind || ["access", "rail", "plane", "deck"].includes(tool);

  if (!showsAnything) {
    return null;
  }

  return (
    <RibbonSection title="Options">
      {tool === "plane" && planeOptionButtons(planes)}
      {tool === "deck" && deckOptionSlots(state)}

      {tool === "access" && (
        <RibbonSlot size="medium">
          <Select
            value={accKind}
            onChange={(value) => value && setAccessKind(toAccessKind(value))}
            options={ACCESS_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
          />
        </RibbonSlot>
      )}

      {tool === "rail" && (
        <RibbonSlot size="medium">
          <Select
            value={railKind}
            onChange={(value) => value && setRailKind(toRailKind(value))}
            options={RAIL_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
          />
        </RibbonSlot>
      )}

      {tool === "bracket" && (
        <RibbonSlot size="medium">
          <Select
            value={bracketKind}
            onChange={(value) => value && setBracketKind(toBracketKind(value))}
            options={BRACKET_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
          />
        </RibbonSlot>
      )}

      {showsBeamKind && (
        <>
          <RibbonButton
            label="LB"
            size="mini"
            selected={beamKind === "lb"}
            onClick={() => setBeamKind("lb")}
            tooltip="Ledger beam — lattice"
          />
          <RibbonButton
            label="ERB"
            size="mini"
            selected={beamKind === "erb"}
            onClick={() => setBeamKind("erb")}
            tooltip="Single tube beam"
          />
        </>
      )}

      {sizes && (
        <RibbonSlot size="medium">
          <Select
            value={String(size)}
            onChange={(value) => value && setSize(Number(value))}
            options={sizes.map((length) => ({ value: String(length), label: `${length} mm` }))}
          />
        </RibbonSlot>
      )}

      {(tool === "spir" || tool === "beam") && (
        <>
          <RibbonButton
            label="Auto-standard"
            size="mini"
            selected={autoSpir}
            onClick={() => setAutoSpir(!autoSpir)}
            tooltip="Place one standard at a free beam end"
          />
          <RibbonButton
            label="Snap"
            size="mini"
            selected={snap}
            onClick={() => setSnap(!snap)}
            tooltip="Magnetise to standards' axes and module distances"
          />
        </>
      )}
    </RibbonSection>
  );
}

function toAccessKind(value: string): AccessKind {
  if (value === "utv") {
    return "utv";
  }
  if (value === "ladder") {
    return "ladder";
  }

  return "luke";
}

function toRailKind(value: string): RailKind {
  if (value === "skrd") {
    return "skrd";
  }
  if (value === "toe") {
    return "toe";
  }

  return "both";
}

function toBracketKind(value: string): BracketKind {
  return value === "skd" ? "skd" : "sk";
}
